import tokens from '@kluein/design-tokens';
import {
  Anchor,
  Box,
  Button,
  Heading,
  ProgressBar,
  ProgressBarState,
  Skeleton,
  Text,
} from '@kluein/klue-ui';
import { CheckCircle } from '@phosphor-icons/react';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { KlueAILogo } from 'components/common/KlueAILogo';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import type { KnowledgeHubSteps } from 'api/endpoints/knowledge-hub/knowledge-hub.types';

type KnowledgeHubChecklistProps = {
  progress: number;
  currentStep: KnowledgeHubSteps[number] | undefined;
  steps: KnowledgeHubSteps;
  isWidget: boolean;
  isLoading: boolean;
};

export function KnowledgeHubChecklistSkeleton() {
  return (
    <Box>
      <Box
        pad={{
          horizontal: 'large',
          vertical: 'xxlarge',
        }}
        border={{ side: 'bottom', color: tokens.color.primitives.gray[100] }}
      >
        <Box width="40%">
          <Skeleton.Line height="18px" numberOfLines={1} />
        </Box>
        <Box margin={{ top: 'small' }} width="70%">
          <Skeleton.Line height="16px" numberOfLines={1} />
        </Box>
        <Box
          margin={{ top: 'medium' }}
          width="100%"
          direction="row"
          gap="small"
        >
          <Box width="90px">
            <Skeleton.Line height="16px" numberOfLines={1} />
          </Box>

          <Box width="calc(100% - 90px)">
            <Skeleton.Line height="8px" numberOfLines={1} />
          </Box>
        </Box>
      </Box>

      <Box pad={{ horizontal: 'large' }}>
        {[...Array(2)].map((_, index) => {
          const isLast = index === 1;

          return (
            <Box
              key={index}
              pad={{
                vertical: 'large',
              }}
              border={{
                side: 'bottom',
                color: isLast
                  ? 'transparent'
                  : tokens.color.primitives.gray[100],
              }}
              direction="row"
              align="center"
              gap="medium"
            >
              <Box width="24px" height="24px">
                <Skeleton.Circle />
              </Box>

              <Box flex="grow">
                <Box width="60%">
                  <Skeleton.Line height="16px" numberOfLines={1} />
                </Box>
              </Box>

              {!isLast && (
                <Box height="26px">
                  <Skeleton.Square size="60px" animated />
                </Box>
              )}
            </Box>
          );
        })}

        <Box
          alignSelf="end"
          margin={{ bottom: 'large', right: 'large', top: 'xxsmall' }}
          width="40px"
        >
          <Skeleton.Line height="16px" numberOfLines={1} />
        </Box>
      </Box>
    </Box>
  );
}

export function KnowledgeHubChecklist({
  progress,
  currentStep,
  steps,
  isWidget,
  isLoading,
}: KnowledgeHubChecklistProps) {
  const { t } = useTranslation(['KnowledgeHub']);
  const { current: isStarted } = useRef(progress > 0);
  const navigate = useNavigate();

  const checklistSteps = useMemo(() => {
    return steps.filter((step) => step.name !== 'done');
  }, [steps]);

  if (isLoading) {
    return <KnowledgeHubChecklistSkeleton />;
  }

  if (!checklistSteps || !currentStep) {
    return null;
  }

  return (
    <Box data-test-id={TEST_IDS.knowledgeHub.checklist.container}>
      <Box
        pad={{
          horizontal: 'large',
          vertical: 'xxlarge',
        }}
        border={{ side: 'bottom', color: tokens.color.primitives.gray[100] }}
      >
        <Heading level={3}>
          {t(
            `KnowledgeHub:checklist.${
              isStarted ? 'started' : 'notStarted'
            }.title`,
          )}
        </Heading>
        <Text margin={{ bottom: 'medium' }}>
          {t('KnowledgeHub:checklist.subTitle')}
        </Text>
        <ProgressBar
          textPosition="inline"
          hideLabel
          a11yTitle={t(
            `KnowledgeHub:checklist.${
              isStarted ? 'started' : 'notStarted'
            }.title`,
          )}
          helperText={`${progress}% complete`}
          value={progress || 0}
          max={100}
          min={0}
          indent="none"
          state={ProgressBarState.ACTIVE}
          disablePulse
        />
      </Box>
      <Box pad={{ horizontal: 'large' }}>
        <Box>
          {checklistSteps.map((item, index) => {
            const isDone = item.status === 'done';
            const isCurrent = item.name === currentStep.name;
            const isDisabled = item.blocked;
            // const isAnswerImprovements = item.name === 'askKlue';
            const isAnswerImprovements = false;
            const position = index + 1;

            return (
              <Box
                key={item.name}
                pad={{
                  vertical: 'large',
                }}
                border={{
                  side: 'bottom',
                  color:
                    index === checklistSteps.length - 1
                      ? 'transparent'
                      : tokens.color.primitives.gray[100],
                }}
                direction="row"
                align="center"
                gap="2px"
              >
                <Box direction="row" align="center">
                  {(() => {
                    if (isDone) {
                      return (
                        <CheckCircle
                          size="30px"
                          weight="fill"
                          color={tokens.color.primitives.blue[450]}
                        />
                      );
                    }

                    return (
                      <Box
                        width="24px"
                        height="24px"
                        round="24px"
                        justify="center"
                        align="center"
                        margin={{ horizontal: '6px' }}
                        background={
                          isCurrent
                            ? tokens.color.primitives.blue[50]
                            : 'transparent'
                        }
                        border={{
                          size: '1px',
                          color: isCurrent
                            ? tokens.color.primitives.blue[450]
                            : tokens.color.primitives.gray[200],
                        }}
                      >
                        <Text
                          size="10px"
                          color={
                            isCurrent
                              ? tokens.color.primitives.blue[450]
                              : tokens.color.primitives.gray[425]
                          }
                        >
                          {position}
                        </Text>
                      </Box>
                    );
                  })()}
                </Box>
                <Anchor
                  onClick={() => navigate(item.url)}
                  disabled={isDisabled}
                  color={tokens.color.primitives.gray[!isDisabled ? 900 : 425]}
                  data-tracking-id={TRACKING_IDS.knowledgeHub.checklist.link}
                >
                  <Text
                    fontWeight="semibold"
                    size="medium"
                    style={{ textDecoration: isDone ? 'line-through' : 'none' }}
                  >
                    {t(item.title)}
                  </Text>
                </Anchor>
                {isWidget ? (
                  <Box margin={{ left: 'auto' }}>
                    {(() => {
                      if (isDone) {
                        return (
                          <Button
                            size="small"
                            variant="primary"
                            onClick={() => navigate(item.url)}
                            data-tracking-id={
                              TRACKING_IDS.knowledgeHub.checklist.callToAction
                            }
                          >
                            Review
                          </Button>
                        );
                      }

                      if (isCurrent && !isDisabled) {
                        return (
                          <Button
                            size="small"
                            variant="highlight-action"
                            onClick={() => navigate(item.url)}
                            data-tracking-id={
                              TRACKING_IDS.knowledgeHub.checklist.callToAction
                            }
                          >
                            {isAnswerImprovements
                              ? t(
                                  'KnowledgeHub:checklist.askKlueAnswerImprovements.callToAction',
                                )
                              : t('KnowledgeHub:checklist.callToAction')}
                          </Button>
                        );
                      }

                      return null;
                    })()}
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Box>
        {isWidget ? (
          <Box alignSelf="end" margin={{ bottom: 'large' }}>
            <KlueAILogo
              klueIconSize={tokens.iconSize.medium}
              aiIconSize={tokens.iconSize.small}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
